import React, { useState, useRef, useEffect } from 'react';
import { SelectionGrid } from './SelectionGrid';
import { useIsMobile, useIsTablet } from '../../utils/useMediaQuery';
import { CountryInput } from '../common/CountryInput';

const initialFormData = {
    name: '',
    email: '',
    phone: '',
    country: 'Canada',
    city: '',
    companyName: '',
    companyWebsite: '',
    projectDetails: '',
    helpNeeded: [],
};

const helpOptions = [
    'Pricing guidance',
    'Layout Planning',
    'Material selection',
    'Other',
];

export function ReviewForm({ isOpen, selections, onClose, onSubmit, isSubmitting }) {
    const [formData, setFormData] = useState(initialFormData);
    const [activeTab, setActiveTab] = useState('form');

    const panelRef = useRef(null);
    const isMobile = useIsMobile();
    const isTablet = useIsTablet();
    const isMobileDevice = isMobile || isTablet;

    // Reset form state when panel closes
    useEffect(() => {
        if (!isOpen) {
            setFormData(initialFormData);
            setActiveTab('form');
        }
    }, [isOpen]);

    // Focus trap + Escape key
    useEffect(() => {
        if (!isOpen) return;

        const panel = panelRef.current;
        if (!panel) return;

        const firstFocusable = panel.querySelector(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        firstFocusable?.focus();

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
                return;
            }
            if (e.key !== 'Tab') return;

            const focusable = [...panel.querySelectorAll(
                'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )];
            if (focusable.length === 0) return;

            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (e.shiftKey) {
                if (document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
            } else {
                if (document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleChangeCountry = (name) => {
        setFormData(prev => ({ ...prev, country: name }));
    };

    const handleCheckboxChange = (value) => {
        setFormData(prev => ({
            ...prev,
            helpNeeded: prev.helpNeeded.includes(value)
                ? prev.helpNeeded.filter(item => item !== value)
                : [...prev.helpNeeded, value],
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);

    };

    return (
        <>
            {isOpen && (
                <button
                    onClick={onClose}
                    aria-label="Close panel"
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.1)',
                        backdropFilter: 'blur(5px)',
                        zIndex: 9998,
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        transition: 'opacity 0.3s',
                    }}
                />
            )}

            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-label="Contact / Project Review"
                tabIndex={-1}
                inert={!isOpen || undefined}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: '#fafafa',
                    transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
                    transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    zIndex: 10000,
                    overflowY: 'auto',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    outline: 'none',
                }}
            >
                {/* Success view */}
                <>
                        {/* Mobile Tabs */}
                        {isMobileDevice && (
                            <div style={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                                background: '#fff',
                                borderBottom: '2px solid #e5e5e5',
                                display: 'flex',
                            }}>
                                <button
                                    onClick={() => setActiveTab('form')}
                                    style={{
                                        flex: 1,
                                        padding: '16px',
                                        background: activeTab === 'form' ? '#fff' : '#f5f5f5',
                                        border: 'none',
                                        borderBottom: activeTab === 'form' ? '2px solid #1a1a1a' : 'none',
                                        marginBottom: '-2px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: activeTab === 'form' ? '600' : '400',
                                        color: activeTab === 'form' ? '#1a1a1a' : '#666',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    Contact Form
                                </button>
                                <button
                                    onClick={() => setActiveTab('selections')}
                                    style={{
                                        flex: 1,
                                        padding: '16px',
                                        background: activeTab === 'selections' ? '#fff' : '#f5f5f5',
                                        border: 'none',
                                        borderBottom: activeTab === 'selections' ? '2px solid #1a1a1a' : 'none',
                                        marginBottom: '-2px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: activeTab === 'selections' ? '600' : '400',
                                        color: activeTab === 'selections' ? '#1a1a1a' : '#666',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    Selections ({selections.length})
                                </button>
                                <button
                                    onClick={onClose}
                                    aria-label="Close"
                                    style={{
                                        padding: '16px',
                                        background: '#1A1A1A',
                                        border: 'none',
                                        borderBottom: 'none',
                                        marginBottom: '-2px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        color: '#f5f5f5',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        width: '55px',
                                    }}
                                >
                                    ✕
                                </button>
                            </div>
                        )}

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: isMobileDevice ? '1fr' : '1fr 1fr',
                            minHeight: isMobileDevice ? 'auto' : '100dvh',
                        }}>
                            {/* Left side - Form */}
                            <div style={{
                                padding: isMobileDevice ? '20px' : '60px 80px',
                                background: '#ffffff',
                                borderRight: isMobileDevice ? 'none' : '1px solid #e5e5e5',
                                position: 'relative',
                                display: isMobileDevice ? (activeTab === 'form' ? 'block' : 'none') : 'block',
                                boxSizing: 'border-box',
                            }}>
                                {!isMobileDevice && (
                                    <button
                                        onClick={onClose}
                                        aria-label="Close"
                                        style={{
                                            position: 'absolute',
                                            top: '20px',
                                            right: '20px',
                                            background: 'none',
                                            border: 'none',
                                            fontSize: '28px',
                                            cursor: 'pointer',
                                            color: '#666',
                                            lineHeight: 1,
                                            fontWeight: '300',
                                            padding: '8px',
                                            zIndex: 1,
                                        }}
                                    >
                                        ✕
                                    </button>
                                )}
                                <h2 style={{
                                    fontSize: '28px',
                                    fontWeight: '300',
                                    letterSpacing: '2px',
                                    marginBottom: '16px',
                                    color: '#1a1a1a',
                                }}>
                                    CONTACT / PROJECT REVIEW
                                </h2>

                                <form onSubmit={handleSubmit}>
                                    {/* Name */}
                                    <div style={{ marginBottom: isMobileDevice ? '16px' : '20px' }}>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            marginBottom: '8px',
                                            color: '#1a1a1a',
                                        }}>
                                            Name <span style={{ color: '#e00' }}>*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            placeholder="Full name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: isMobileDevice ? '14px 12px' : '12px',
                                                border: '1px solid #d0d0d0',
                                                fontSize: '14px',
                                                minHeight: isMobileDevice ? '48px' : 'auto',
                                                boxSizing: 'border-box',
                                            }}
                                        />
                                    </div>

                                    {/* Email & Phone */}
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: isMobileDevice ? '1fr' : '1fr 1fr',
                                        gap: isMobileDevice ? '16px' : '20px',
                                        marginBottom: isMobileDevice ? '16px' : '20px',
                                    }}>
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                marginBottom: '8px',
                                                color: '#1a1a1a',
                                            }}>
                                                Email <span style={{ color: '#e00' }}>*</span>
                                            </label>
                                            <input
                                                type="email"
                                                name="email"
                                                placeholder="Email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                                style={{
                                                    width: '100%',
                                                    padding: isMobileDevice ? '14px 12px' : '12px',
                                                    border: '1px solid #d0d0d0',
                                                    fontSize: '14px',
                                                    minHeight: isMobileDevice ? '48px' : 'auto',
                                                    boxSizing: 'border-box',
                                                }}
                                            />
                                        </div>

                                        <div>
                                            <label style={{
                                                display: 'block',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                marginBottom: '8px',
                                                color: '#1a1a1a',
                                            }}>
                                                Phone
                                            </label>
                                            <input
                                                type="tel"
                                                name="phone"
                                                placeholder="Phone number"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                style={{
                                                    width: '100%',
                                                    padding: isMobileDevice ? '14px 12px' : '12px',
                                                    border: '1px solid #d0d0d0',
                                                    fontSize: '14px',
                                                    minHeight: isMobileDevice ? '48px' : 'auto',
                                                    boxSizing: 'border-box',
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Country & City */}
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: isMobileDevice ? '1fr' : '1fr 1fr',
                                        gap: isMobileDevice ? '16px' : '20px',
                                        marginBottom: isMobileDevice ? '16px' : '20px',
                                    }}>
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                marginBottom: '8px',
                                                color: '#1a1a1a',
                                            }}>
                                                Country
                                            </label>
                                            <CountryInput
                                                value={formData.country}
                                                onChange={handleChangeCountry}
                                                isMobile={isMobileDevice}
                                            />
                                        </div>

                                        <div>
                                            <label style={{
                                                display: 'block',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                marginBottom: '8px',
                                                color: '#1a1a1a',
                                            }}>
                                                City <span style={{ color: '#e00' }}>*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="city"
                                                placeholder="City"
                                                value={formData.city}
                                                onChange={handleChange}
                                                required
                                                style={{
                                                    width: '100%',
                                                    padding: isMobileDevice ? '14px 12px' : '12px',
                                                    border: '1px solid #d0d0d0',
                                                    fontSize: '14px',
                                                    minHeight: isMobileDevice ? '48px' : 'auto',
                                                    boxSizing: 'border-box',
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Company Name & Website */}
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: isMobileDevice ? '1fr' : '1fr 1fr',
                                        gap: isMobileDevice ? '16px' : '20px',
                                        marginBottom: isMobileDevice ? '16px' : '20px',
                                    }}>
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                marginBottom: '8px',
                                                color: '#1a1a1a',
                                            }}>
                                                Company Name
                                            </label>
                                            <input
                                                type="text"
                                                name="companyName"
                                                placeholder="Company name (if applicable)"
                                                value={formData.companyName}
                                                onChange={handleChange}
                                                style={{
                                                    width: '100%',
                                                    padding: isMobileDevice ? '14px 12px' : '12px',
                                                    border: '1px solid #d0d0d0',
                                                    fontSize: '14px',
                                                    minHeight: isMobileDevice ? '48px' : 'auto',
                                                    boxSizing: 'border-box',
                                                }}
                                            />
                                        </div>

                                        <div>
                                            <label style={{
                                                display: 'block',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                marginBottom: '8px',
                                                color: '#1a1a1a',
                                            }}>
                                                Company Website
                                            </label>
                                            <input
                                                type="url"
                                                name="companyWebsite"
                                                placeholder="https://example.com"
                                                value={formData.companyWebsite}
                                                onChange={handleChange}
                                                style={{
                                                    width: '100%',
                                                    padding: isMobileDevice ? '14px 12px' : '12px',
                                                    border: '1px solid #d0d0d0',
                                                    fontSize: '14px',
                                                    minHeight: isMobileDevice ? '48px' : 'auto',
                                                    boxSizing: 'border-box',
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Project Details */}
                                    <div style={{ marginBottom: isMobileDevice ? '16px' : '20px' }}>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            marginBottom: '8px',
                                            color: '#1a1a1a',
                                        }}>
                                            Project Details
                                        </label>
                                        <textarea
                                            name="projectDetails"
                                            placeholder="Tell us about your space, usage (residential or commercial), timeline, and anything else that will help us tailor our recommendations.&#10;Example: 18' x 22' room, front row sectional, install in 4-6 months"
                                            value={formData.projectDetails}
                                            onChange={handleChange}
                                            rows={isMobileDevice ? 4 : 5}
                                            style={{
                                                width: '100%',
                                                padding: isMobileDevice ? '14px 12px' : '12px',
                                                border: '1px solid #d0d0d0',
                                                fontSize: '14px',
                                                fontFamily: 'inherit',
                                                resize: 'vertical',
                                                minHeight: isMobileDevice ? '140px' : 'auto',
                                                boxSizing: 'border-box',
                                            }}
                                        />
                                    </div>

                                    {/* How Can We Help - Checkboxes */}
                                    <div style={{ marginBottom: isMobileDevice ? '16px' : '20px' }}>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            marginBottom: '12px',
                                            color: '#1a1a1a',
                                        }}>
                                            How Can We Help?
                                        </label>
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: isMobileDevice ? '1fr' : '1fr 1fr',
                                            gap: '12px',
                                        }}>
                                            {helpOptions.map(option => (
                                                <label
                                                    key={option}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        cursor: 'pointer',
                                                        padding: isMobileDevice ? '12px' : '10px',
                                                        border: '1px solid #d0d0d0',
                                                        background: formData.helpNeeded.includes(option) ? '#f0f0f0' : '#fff',
                                                        transition: 'background 0.2s',
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.helpNeeded.includes(option)}
                                                        onChange={() => handleCheckboxChange(option)}
                                                        style={{
                                                            marginRight: '10px',
                                                            width: '18px',
                                                            height: '18px',
                                                            cursor: 'pointer',
                                                            accentColor: '#1a1a1a',
                                                        }}
                                                    />
                                                    <span style={{ fontSize: '14px', color: '#1a1a1a' }}>
                                                        {option}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Submit Button */}
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        style={{
                                            width: '100%',
                                            padding: '16px',
                                            background: '#1a1a1a',
                                            color: '#ffffff',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: '13px',
                                            fontWeight: '600',
                                            letterSpacing: '1px',
                                            textTransform: 'uppercase',
                                            minHeight: isMobileDevice ? '52px' : 'auto',
                                        }}
                                    >
                                        SUBMIT {isMobileDevice && selections.length > 0 && `(${selections.length} items)`}
                                    </button>

                                    {/* Link to view selections on mobile */}
                                    {isMobileDevice && selections.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => setActiveTab('selections')}
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                marginTop: '12px',
                                                background: 'none',
                                                color: '#1A1A1A',
                                                border: '1px solid #1A1A1A',
                                                cursor: 'pointer',
                                                fontSize: '13px',
                                                fontWeight: '500',
                                                letterSpacing: '0.5px',
                                            }}
                                        >
                                            View My Selections ({selections.length})
                                        </button>
                                    )}
                                </form>
                            </div>

                            {/* Right side - Selections */}
                            <div style={{
                                display: isMobileDevice ? (activeTab === 'selections' ? 'block' : 'none') : 'block',
                            }}>
                                <SelectionGrid selections={selections} />
                            </div>
                        </div>
                    </>
            </div>
        </>
    );
}
